import { useEffect, useRef, useState, DragEvent, ChangeEvent } from 'react';
import {
  ShieldAlert,
  Upload,
  Loader2,
  Download,
  AlertCircle,
  CheckCircle,
  FileText,
} from 'lucide-react';
import { redactDocument, type RedactionSummary } from '../api/tools';
import { getPiiStripLock, type PiiStripLock } from '../api/admin';

type Phase = 'idle' | 'working' | 'done' | 'error';

const CATEGORY_LABELS: Record<string, string> = {
  ssn: 'SSN',
  email: 'Email',
  phone: 'Phone',
  credit_card: 'Credit Card',
  isin: 'ISIN/CUSIP',
  account_number: 'Account Number',
  dob: 'Date of Birth',
  date: 'Date',
  address: 'Address',
  zip: 'ZIP Code',
  name: 'Name',
  signature: 'Signature',
};

export default function Tools() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState('');
  const [summary, setSummary] = useState<RedactionSummary | null>(null);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [downloadName, setDownloadName] = useState('');
  const [fileName, setFileName] = useState('');
  const [fast, setFast] = useState(true);
  // PII-strip guard lock. When the private path uses a cloud
  // backend, the Tools PII redaction (fast / regex mode) control is
  // forced ON + disabled.
  const [piiLock, setPiiLock] = useState<PiiStripLock | null>(null);

  useEffect(() => {
    let cancelled = false;
    getPiiStripLock()
      .then((lock) => {
        if (cancelled) return;
        setPiiLock(lock);
        if (lock.locked) setFast(true);
      })
      .catch(() => { /* non-fatal: leave control editable */ });
    return () => { cancelled = true; };
  }, []);

  const reset = () => {
    setPhase('idle');
    setError('');
    setSummary(null);
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    setDownloadUrl('');
    setDownloadName('');
    setFileName('');
  };

  const handleFile = async (file: File) => {
    reset();
    setFileName(file.name);
    setPhase('working');
    try {
      const result = await redactDocument(file, fast);
      const url = URL.createObjectURL(result.blob);
      setDownloadUrl(url);
      setDownloadName(result.filename);
      setSummary(result.summary);
      setPhase('done');
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Redaction request failed.';
      setError(msg);
      setPhase('error');
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const counts = summary?.categories
    ? Object.entries(summary.categories).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Tools</h1>
        <p className="text-sm text-slate-500 mt-1">
          Document utilities for safer deposit-review workflows.
        </p>
      </div>

      {/* PII Redaction card */}
      <section className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <ShieldAlert className="text-amber-600" size={20} />
            <h2 className="text-lg font-semibold text-slate-900">
              PII / Signature Redaction
            </h2>
          </div>
          <p className="text-sm text-slate-600 mt-2">
            Drop or upload a document containing personally identifiable
            information (PII) and signatures. The tool produces a redacted
            copy with PII and signatures blacked out and marked EDITED, so
            you can safely upload it to the cloud AI.
          </p>
          <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            Best-effort only (patterns + heuristics). Reduces PII exposure but
            is not a guarantee. Always review the redacted output before
            uploading to any public endpoint.
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Drop zone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className="cursor-pointer rounded-lg border-2 border-dashed border-slate-300 hover:border-primary-400 hover:bg-primary-50/40 transition-colors py-10 flex flex-col items-center justify-center text-slate-500"
          >
            <Upload size={28} className="text-slate-400" />
            <p className="mt-2 text-sm font-medium text-slate-700">
              Drag &amp; drop a document here, or click to browse
            </p>
            <p className="text-xs text-slate-400 mt-1">
              PDF, DOCX, TXT, PNG/JPG (up to 25 MB)
            </p>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={onPick}
              accept=".pdf,.docx,.txt,.text,.csv,.md,.png,.jpg,.jpeg,.tif,.tiff,.bmp,.gif"
            />
          </div>

          {/* Fast mode toggle */}
          <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={fast}
              onChange={(e) => setFast(e.target.checked)}
              disabled={phase === 'working' || (piiLock?.locked ?? false)}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium text-slate-700">Fast mode (patterns only)</span>
              <span className="block text-xs text-slate-400">
                Instant redaction using pattern matching (catches PII + signature-line names). Uncheck for AI-assisted
                thorough mode (best quality, but can take 30-90s; slow for large docs).
              </span>
              {piiLock?.locked && (
                <span className="block text-xs text-amber-700 mt-1 font-medium">
                  Locked ON: required when the private path uses a cloud backend
                  ({piiLock.private_backend_type}).
                </span>
              )}
            </span>
          </label>

          {phase === 'working' && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="animate-spin" size={18} />
              <span>Redacting &ldquo;{fileName}&rdquo;&hellip;</span>
            </div>
          )}

          {phase === 'error' && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Redaction failed</p>
                <p className="text-xs">{error}</p>
              </div>
            </div>
          )}

          {phase === 'done' && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700">
                <CheckCircle size={18} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Redaction complete</p>
                  <p className="text-xs">
                    Output marked EDITED. Review it before any public upload.
                  </p>
                </div>
              </div>

              {summary?.note && (
                <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                  {summary.note}
                </div>
              )}

              {/* Summary */}
              <div className="rounded-md border border-slate-200">
                <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    Redacted (category counts)
                  </p>
                </div>
                <div className="p-3">
                  {counts.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {counts.map(([cat, n]) => (
                        <span
                          key={cat}
                          className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                        >
                          {CATEGORY_LABELS[cat] || cat}
                          <span className="text-slate-400">x{n}</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">
                      No PII/signature regions detected. Output is still
                      marked EDITED.
                    </p>
                  )}
                  <p className="text-xs text-slate-400 mt-2">
                    Method: {summary?.method || 'text'}
                  </p>
                </div>
              </div>

              {/* Download */}
              <div className="flex items-center gap-3">
                <a
                  href={downloadUrl}
                  download={downloadName}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
                >
                  <Download size={16} />
                  Download redacted document
                </a>
                <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                  <FileText size={14} />
                  {downloadName}
                </span>
                <button
                  onClick={reset}
                  className="ml-auto text-sm text-slate-500 hover:text-slate-700"
                >
                  Redact another
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}